import React, { forwardRef, useCallback } from 'react';
import { MessageType, TopicType } from '@/types/assistant';
import { ChatScrollArea, ChatScrollAreaRef } from './ChatScrollArea';
import { ChatMessage } from './ChatMessage';
import { TypingIndicator } from './TypingIndicator';
import { LoadMoreButton } from './LoadMoreButton';
import { ScrollToBottomButton } from './ScrollToBottomButton';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface MessageContainerProps {
  messages: MessageType[];
  hasMoreMessages: boolean;
  isTyping: boolean;
  isLoading: boolean;
  isLoadingMoreMessages: boolean;
  topic: TopicType;
  onFeedbackChange?: (messageId: string, feedback: 'positive' | 'negative' | null) => void;
  onSuggestionClick?: (suggestion: string) => void;
  onLoadMore: () => Promise<boolean>;
  showSuggestions: boolean;
  onScrollNearBottom: (isNear: boolean) => void;
  onScrollNearTop: () => void;
  scrollAreaRef: React.RefObject<ChatScrollAreaRef>;
  onRegenerateMessage?: (messageId: string) => void;
  isNearBottom: boolean;
  onScrollToBottom: () => void;
}

export const MessageContainer = forwardRef<ChatScrollAreaRef, MessageContainerProps>(
  ({
    messages,
    hasMoreMessages,
    isTyping,
    isLoading,
    isLoadingMoreMessages,
    topic,
    onFeedbackChange,
    onSuggestionClick,
    onLoadMore,
    showSuggestions,
    onScrollNearBottom,
    onScrollNearTop,
    scrollAreaRef,
    onRegenerateMessage,
    isNearBottom,
    onScrollToBottom
  }, ref) => {
    const isMobile = useIsMobile();

    const handleLoadMore = useCallback(async () => {
      try {
        return await onLoadMore();
      } catch (error) {
        console.error('Error in MessageContainer.handleLoadMore:', error);
        return false;
      }
    }, [onLoadMore]);

    // Show scroll button when not near bottom and has messages
    const showScrollButton = React.useMemo(() => {
      const shouldShow = !isNearBottom && messages.length > 3 && !isLoadingMoreMessages;
      console.log('MessageContainer scroll button logic:', {
        isNearBottom,
        messagesLength: messages.length,
        isLoadingMoreMessages,
        shouldShow
      });
      return shouldShow;
    }, [isNearBottom, messages.length, isLoadingMoreMessages]);

    const handleScrollToBottom = useCallback(() => {
      console.log('MessageContainer handleScrollToBottom called');
      onScrollToBottom();
    }, [onScrollToBottom]);

    return (
      <div className="flex-1 relative overflow-hidden" style={{ pointerEvents: 'auto' }}>
        <ChatScrollArea
          ref={scrollAreaRef}
          className={cn(
            "h-full momentum-scroll ios-scroll-fix",
            // Enhanced mobile scrolling
            "overscroll-contain touch-pan-y"
          )}
          onScrollNearBottom={onScrollNearBottom}
          onScrollNearTop={onScrollNearTop}
          bottomThreshold={200}
        >
          <div className={cn(
            "flex flex-col px-4 py-6 max-w-4xl mx-auto",
            "min-h-full"
          )} 
          style={{ 
            pointerEvents: 'auto',
            // Enhanced padding-top calculation for fixed topbar
            paddingTop: isMobile 
              ? 'calc(var(--topbar-height-mobile) + var(--safe-area-inset-top) + 1rem)' 
              : 'calc(var(--topbar-height) + var(--safe-area-inset-top) + 1.25rem)',
            paddingBottom: 'calc(6rem + var(--safe-area-inset-bottom))'
          }}>
            {/* Load more messages section */}
            <LoadMoreButton
              hasMoreMessages={hasMoreMessages}
              isLoadingMore={isLoadingMoreMessages}
              onLoadMore={handleLoadMore}
            />

            {/* Messages list */}
            <div className="flex-1 space-y-1">
              {messages.map((message, index) => (
                <ChatMessage
                  key={`${message.id}-${message.is_streaming ? 'streaming' : 'final'}`}
                  message={message}
                  topic={topic}
                  isLatest={index === messages.length - 1}
                  onSuggestionClick={showSuggestions ? onSuggestionClick : undefined}
                  onFeedbackChange={onFeedbackChange}
                  onRegenerateMessage={onRegenerateMessage}
                />
              ))}
            </div>

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start mt-4">
                <div className="max-w-2xl">
                  <TypingIndicator topic={topic} />
                </div>
              </div>
            )}
          </div>
        </ChatScrollArea>

        {/* Scroll to bottom button - positioned above sticky InputFooter */}
        <ScrollToBottomButton
          isVisible={showScrollButton}
          onClick={handleScrollToBottom}
          topic={topic}
          className={cn(
            // Enhanced positioning above input footer
            "bottom-16 md:bottom-20 left-1/2 transform -translate-x-1/2"
          )}
          style={{
            bottom: 'calc(5rem + var(--safe-area-inset-bottom))'
          }}
        />
      </div>
    );
  }
);

MessageContainer.displayName = "MessageContainer";
